/**
 * Date formatting utilities for activities
 */

/**
 * Format a single date string
 */
export const formatDate = (dateString: string, options?: {
  weekday?: 'long' | 'short' | 'narrow';
  year?: 'numeric' | '2-digit';
  month?: 'long' | 'short' | 'narrow' | 'numeric' | '2-digit';
  day?: 'numeric' | '2-digit';
}): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  
  return new Date(dateString).toLocaleDateString('en-US', {
    ...defaultOptions,
    ...options,
  });
};

/**
 * Format a date range for multi-day events
 * Returns formatted string like "Jan 15 - Jan 20" or "Jan 15 - Feb 5"
 */
export const formatDateRange = (
  startDate: string,
  endDate?: string | null,
  options?: {
    includeYear?: boolean;
    sameMonthFormat?: 'short' | 'long';
  }
): string => {
  if (!endDate || startDate === endDate) {
    // Single day event
    return formatDate(startDate, {
      month: 'short',
      day: 'numeric',
      ...(options?.includeYear && { year: 'numeric' }),
    });
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  // Same month and year
  if (startMonth === endMonth && startYear === endYear) {
    if (options?.includeYear) {
      return `${startMonth} ${startDay} - ${endDay}, ${startYear}`;
    }
    return `${startMonth} ${startDay} - ${endDay}`;
  }

  // Same year, different months
  if (startYear === endYear) {
    if (options?.includeYear) {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }

  // Different years
  return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
};

/**
 * Format time string
 */
export const formatTime = (timeString: string): string => {
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date and time for activity display
 * Handles both single-day and multi-day events
 */
export const formatActivityDate = (
  activity: {
    date: string;
    end_date?: string | null;
    endDate?: string | null;
    is_multi_day?: boolean;
    isMultiDay?: boolean;
    time?: string;
    end_time?: string | null;
  },
  options?: {
    includeYear?: boolean;
  }
): string => {
  const endDate = activity.end_date || activity.endDate;
  const isMultiDay = activity.is_multi_day || activity.isMultiDay || !!endDate;
  
  if (isMultiDay && endDate) {
    return formatDateRange(activity.date, endDate, options);
  }
  
  return formatDate(activity.date, options?.includeYear ? {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  } : undefined);
};

/**
 * Format date and time together for activity display
 */
export const formatActivityDateTime = (activity: {
  date: string;
  end_date?: string | null;
  endDate?: string | null;
  is_multi_day?: boolean;
  isMultiDay?: boolean;
  time?: string;
  end_time?: string | null;
}): string => {
  const dateStr = formatActivityDate(activity);
  const timeStr = activity.time ? formatTime(activity.time) : '';
  const endTimeStr = activity.end_time ? formatTime(activity.end_time) : '';
  
  if (timeStr && endTimeStr) {
    return `${dateStr} • ${timeStr} - ${endTimeStr}`;
  } else if (timeStr) {
    return `${dateStr} • ${timeStr}`;
  }
  
  return dateStr;
};

